import React from 'react';
import { Card, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

interface AverageReturnTableProps {
  returnsAnalysis: any;
}

interface TableData {
  key: string;
  rank: string;
  forward10: { value: number; color: string };
  forward20: { value: number; color: string };
  forward50: { value: number; color: string };
  forward100: { value: number; color: string };
}

const AverageReturnTable: React.FC<AverageReturnTableProps> = ({ returnsAnalysis }) => {
  const { t } = useTranslation();

  // 根据平均收益确定颜色
  const getReturnColor = (return_: number): string => {
    if (return_ >= 2.0) return '#52c41a'; // 深绿色 - 高收益
    if (return_ >= 1.0) return '#73d13d'; // 绿色 - 正收益
    if (return_ >= 0.0) return '#bae637'; // 浅绿色 - 微正收益
    if (return_ >= -1.0) return '#faad14'; // 黄色 - 微负收益
    if (return_ >= -2.0) return '#ff7a45'; // 橙色 - 负收益
    return '#ff4d4f'; // 红色 - 大负收益
  };

  // 准备表格数据
  const prepareTableData = (): TableData[] => {
    if (!returnsAnalysis?.returns_analysis) return [];

    const rankGroups = ['top_3', 'top_10', 'top_20', 'top_50', 'top_100'];
    const forwardPeriods = ['forward_10', 'forward_20', 'forward_50', 'forward_100'];

    return rankGroups.map((rankGroup) => {
      const rankLabel = rankGroup.replace('top_', 'Top ');
      const rowData: TableData = {
        key: rankGroup,
        rank: rankLabel,
        forward10: { value: 0, color: '#ff4d4f' },
        forward20: { value: 0, color: '#ff4d4f' },
        forward50: { value: 0, color: '#ff4d4f' },
        forward100: { value: 0, color: '#ff4d4f' }
      };

      forwardPeriods.forEach((period) => {
        const periodData = returnsAnalysis.returns_analysis[period];
        if (periodData && periodData[rankGroup]) {
          const avgReturn = periodData[rankGroup].mean_return || 0;
          const columnKey = period.replace('forward_', 'forward') as keyof TableData;
          (rowData[columnKey] as any) = {
            value: avgReturn,
            color: getReturnColor(avgReturn)
          };
        }
      });

      return rowData;
    });
  };

  const tableData = prepareTableData();

  // 自定义单元格渲染
  const renderCell = (value: { value: number; color: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: value.color,
          marginRight: 8,
          border: '1px solid #d9d9d9'
        }}
      />
      <span style={{ 
        fontWeight: 'bold',
        color: value.value >= 0 ? '#52c41a' : '#ff4d4f'
      }}>
        {value.value >= 0 ? '+' : ''}{value.value.toFixed(2)}%
      </span>
    </div>
  );

  const columns = [
    {
      title: t('analysis.similarityRank'),
      dataIndex: 'rank',
      key: 'rank',
      width: 100,
      render: (text: string) => (
        <div style={{ fontWeight: 'bold', color: '#1890ff' }}>{text}</div>
      )
    },
    {
      title: '10 K-lines',
      dataIndex: 'forward10',
      key: 'forward10',
      width: 120,
      render: (value: { value: number; color: string }) => renderCell(value)
    },
    {
      title: '20 K-lines',
      dataIndex: 'forward20',
      key: 'forward20',
      width: 120,
      render: (value: { value: number; color: string }) => renderCell(value)
    },
    {
      title: '50 K-lines',
      dataIndex: 'forward50',
      key: 'forward50',
      width: 120,
      render: (value: { value: number; color: string }) => renderCell(value)
    },
    {
      title: '100 K-lines',
      dataIndex: 'forward100',
      key: 'forward100',
      width: 120,
      render: (value: { value: number; color: string }) => renderCell(value)
    }
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <Title level={5} style={{ textAlign: 'center', marginBottom: 16 }}>
        📈 {t('analysis.averageReturnTable')}
      </Title>
      
      <Card size="small" style={{ borderRadius: 8 }}>
        <Table
          dataSource={tableData}
          columns={columns}
          pagination={false}
          size="small"
          style={{ 
            backgroundColor: '#f5f5f5',
            borderRadius: 8
          }}
        />
        
        {/* 图例说明 */}
        <div style={{ 
          marginTop: 16, 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 24,
          fontSize: '12px',
          color: '#666',
          flexWrap: 'wrap'
        }}>
          {/* <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#52c41a',
              marginRight: 4
            }} />
            <span>{t('analysis.highReturn')} (≥2.0%)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#73d13d',
              marginRight: 4
            }} />
            <span>{t('analysis.positiveReturn')} (1.0-2.0%)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#bae637',
              marginRight: 4
            }} />
            <span>{t('analysis.slightPositive')} (0-1.0%)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#faad14',
              marginRight: 4
            }} />
            <span>{t('analysis.slightNegative')} (-1.0-0%)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#ff7a45',
              marginRight: 4
            }} />
            <span>{t('analysis.negativeReturn')} (-2.0--1.0%)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#ff4d4f',
              marginRight: 4
            }} />
            <span>{t('analysis.highLoss')} (&lt;-2.0%)</span>
          </div> */}
        </div>
      </Card>
    </div>
  );
};

export default AverageReturnTable; 